import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { LoginResponse } from '@/api/types/auth';
import { DEFAULT_LOCALE_TAG } from '@/config/i18n/locale';
import { queryKeys } from '@/config/query/queryKeys';

type User = LoginResponse['user'];

/**
 * Returns the locale tag and timezone every UI date-formatter should pass to
 * `Intl.DateTimeFormat` / `Date.toLocaleString`. Reads from i18next (the
 * authoritative UI language source post-reconciliation) and the cached
 * authenticated user (for timezone). `timeZone` may be undefined when the
 * timezone reconciler hasn't run yet — callers should let `Intl` fall back to
 * the browser default in that case.
 */
export function useUserLocaleAndTimezone(): {
  locale: string;
  timeZone: string | undefined;
} {
  const { i18n } = useTranslation();
  const { data: user } = useQuery<User>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });
  return {
    locale: i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE_TAG,
    timeZone: user?.timezone ?? undefined,
  };
}
